/**
 * Create: row fields plus the document a new assignment is born with (`data` is required and `[]` is
 * a 422). The document is minted by the editor store's `createEmptyDocument` so the `POST` body and
 * the working document cannot drift.
 *
 * The API creates the learning material behind the template, so the form only asks which type; the
 * type also picks the document shape (a chapter for READING, an exercise for MATH).
 */
import type { Ref } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { Grade } from './enums'

import { gradeOptions } from '@shared/lib/format'

import { AssignmentStatus, AssignmentType } from './enums'
import { useAssignmentEditorStore } from './store'
import { useAssignmentTemplatesStore } from '../assignment-templates/store'
import { useAssignmentTemplateStoreApi } from '../../api/assignment-templates/store.hook'
import {
  assignmentTypeOptions,
  getAssignmentEditPath,
  getDisciplineCodeForType,
  isAssignmentTypeAuthorable,
  isTemplateTitleValid,
} from './assignment-template-options'

interface ModalCreateAssignmentTemplateReturn {
  refIsOpen: ComputedRef<boolean>
  refIsLoading: Ref<boolean>
  refTitle: Ref<string>
  refType: Ref<AssignmentType | undefined>
  refGrade: Ref<Grade | undefined>
  refIsValid: ComputedRef<boolean>
  typeOptions: AppSelectOption<AssignmentType>[]
  gradeSelectOptions: AppSelectOption<Grade>[]
  setTitle: (value: string | number | null) => void
  closeModal: () => void
  createTemplate: () => Promise<void>
}

export const useModalCreateAssignmentTemplate = (): ModalCreateAssignmentTemplateReturn => {
  const router = useRouter()
  const store = useAssignmentTemplatesStore()
  const editorStore = useAssignmentEditorStore()

  const refIsLoading = ref(false)
  const refTitle = ref('')
  const refType = ref<AssignmentType | undefined>(AssignmentType.Reading)
  const refGrade = ref<Grade | undefined>()

  const refIsOpen = computed<boolean>(() => store.isModalCreate)

  const refIsValid = computed<boolean>(() =>
    isTemplateTitleValid(refTitle.value)
    && !!refType.value
    && isAssignmentTypeAuthorable(refType.value)
    && !!refGrade.value)

  const setTitle = (value: string | number | null): void => {
    refTitle.value = String(value ?? '')
  }

  const closeModal = (): void => {
    store.setModalCreate(false)
    refTitle.value = ''
    refType.value = AssignmentType.Reading
    refGrade.value = undefined
  }

  const createTemplate = async (): Promise<void> => {
    const type = refType.value
    const grade = refGrade.value

    if (!refIsValid.value || refIsLoading.value || !type || !grade) return

    const disciplineCode = getDisciplineCodeForType(type)

    const title = refTitle.value.trim()
    const document = editorStore.createEmptyDocument({ type, title, disciplineCode, grade })

    const created = await useAssignmentTemplateStoreApi({
      payload: {
        title,
        type,
        status: AssignmentStatus.Draft,
        grade,
        cover_path: null,
        curriculum_topic_id: null,
        curriculum_lesson_id: null,
        data: document,
      },
      onStart: () => { refIsLoading.value = true },
      onFinish: () => { refIsLoading.value = false },
    })

    if (!created) {
      editorStore.clear()
      return
    }

    closeModal()
    await router.push(getAssignmentEditPath(created.id))
  }

  return {
    refIsOpen,
    refIsLoading,
    refTitle,
    refType,
    refGrade,
    refIsValid,
    typeOptions: assignmentTypeOptions,
    gradeSelectOptions: gradeOptions,
    setTitle,
    closeModal,
    createTemplate,
  }
}
